import * as Sentry from '@sentry/react';
import { FRONTEND_VERSION } from '../config/constants';
import { getDeploymentTarget } from '../config/deployment';
import { logger } from '../utils/core/logger';
import { getConsentMode, subscribeConsentMode } from './consentMode';

const POSTHOG_BLOB_ERROR_TYPE = 'PostHogInitializationError';
const POSTHOG_BLOB_ERROR_CODE = 'POSTHOG_INIT_BLOB_UNAVAILABLE';
const SENTRY_BUNDLER_APPLICATION_KEY_FALLBACK = 'inboundx-widget';
const WIDGET_OWNED_SCRIPT_ALLOW_URLS: Array<string | RegExp> = [
    /^https?:\/\/(?:cdn\.)?userose\.ai\//i,
    /^https?:\/\/storage\.googleapis\.com\/inboundx-cdn\/widget\//i,
    /^https?:\/\/(?:localhost|127\.0\.0\.1|0\.0\.0\.0)(?::\d+)?\//i,
];
const EXTENSION_URL_PREFIXES = ['chrome-extension://', 'moz-extension://'] as const;

// Store our Sentry instance separately to avoid conflicts with host page's Sentry
let inboundxSentryClient: ReturnType<typeof Sentry.getClient> | null = null;
let lastConsentPromotion = 0;
subscribeConsentMode((mode, previous) => {
    if (mode === 'full' && previous !== 'full') lastConsentPromotion = Date.now() / 1000;
    if (mode !== 'full') Sentry.setUser(null);
});

/** Rebuild rather than blacklist: error messages, arbitrary context, breadcrumbs and
 * request URLs can contain conversation text or identifiers. Preserve only code location.
 */
export function scrubPrivateSentryEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
    return {
        type: event.type,
        event_id: event.event_id,
        timestamp: event.timestamp,
        platform: 'javascript',
        level: 'error',
        release: event.release,
        environment: event.environment,
        exception: {
            values: event.exception?.values?.map((exception) => ({
                type: 'Error',
                value: 'Error details removed in private mode',
                stacktrace: {
                    frames: exception.stacktrace?.frames?.map((frame) => ({
                        filename:
                            frame.filename && isWidgetOwnedScriptUrl(frame.filename)
                                ? frame.filename.split(/[?#]/)[0]
                                : undefined,
                        lineno: frame.lineno,
                        colno: frame.colno,
                    })),
                },
            })),
        },
    };
}

function getEventFrameFilenames(event: Sentry.Event): string[] {
    const frames =
        event.exception?.values?.flatMap((exception) => exception.stacktrace?.frames ?? []) ?? [];
    return frames
        .map((frame) => frame.filename)
        .filter((filename): filename is string => typeof filename === 'string');
}

function isHttpUrl(url: string): boolean {
    return url.startsWith('http://') || url.startsWith('https://');
}

function isWidgetOwnedScriptUrl(url: string): boolean {
    if (!isHttpUrl(url)) {
        return false;
    }

    return WIDGET_OWNED_SCRIPT_ALLOW_URLS.some((pattern) =>
        pattern instanceof RegExp ? pattern.test(url) : url.includes(pattern)
    );
}

function isBrowserExtensionUrl(url: string): boolean {
    return EXTENSION_URL_PREFIXES.some((prefix) => url.startsWith(prefix));
}

/** A string property of an object-like value (an exception, `event.extra`), else undefined. */
function stringProp(value: unknown, key: string): string | undefined {
    if (typeof value !== 'object' || value === null) {
        return undefined;
    }
    const prop = (value as Record<string, unknown>)[key];
    return typeof prop === 'string' ? prop : undefined;
}

function getPosthogInitErrorCode(hint: Sentry.EventHint | undefined): string | undefined {
    const code = (hint?.originalException as { code?: unknown } | undefined)?.code;
    return typeof code === 'string' ? code : undefined;
}

const ABORT_ERROR_NAMES = ['AbortError', 'RequestCancelledError'];
const ABORT_MESSAGE_NEEDLES = [
    'operation was aborted',
    'signal is aborted',
    'navigation or unmount',
];

/**
 * Navigation/unmount aborts are not real failures. They surface as a native
 * DOMException, a plain object after Sentry serialization, or a wrapped Error with
 * a minified message like `'AbortError: The operation was aborted. '`, so match
 * loosely on the original exception's name and message and on the event's value.
 */
function isAbortError(event: Sentry.Event, hint: Sentry.EventHint | undefined): boolean {
    const original = hint?.originalException;
    const name = stringProp(original, 'name') ?? '';
    const message =
        typeof original === 'string' ? original : (stringProp(original, 'message') ?? '');
    const firstExceptionValue = event.exception?.values?.[0]?.value || '';
    return (
        ABORT_ERROR_NAMES.includes(name) ||
        ABORT_MESSAGE_NEEDLES.some(
            (needle) => message.includes(needle) || firstExceptionValue.includes(needle)
        )
    );
}

// Crawlers/bots inflate error counts (Realm-crawler 1.0 once made ~77% of config-load errors).
// GoogleOther has no "bot" token (ROSE-FRONT-1Q3: its GTM fetch failures blamed the widget).
const CRAWLER_USER_AGENT =
    /bot|crawl|spider|slurp|Realm-crawler|GoogleOther|facebookexternalhit|Twitterbot|LinkedInBot|WhatsApp|Applebot|Baiduspider|DuckDuckBot|Semrush|AhrefsBot|MJ12bot|Screaming Frog/i;

function isCrawlerSession(): boolean {
    return CRAWLER_USER_AGENT.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');
}

function isPosthogBlobUnavailableError(
    event: Sentry.Event,
    hint: Sentry.EventHint | undefined
): boolean {
    const firstException = event.exception?.values?.[0];
    return (
        firstException?.type === POSTHOG_BLOB_ERROR_TYPE &&
        (getPosthogInitErrorCode(hint) === POSTHOG_BLOB_ERROR_CODE ||
            firstException.value?.includes(POSTHOG_BLOB_ERROR_CODE) === true)
    );
}

/**
 * Auto captures from the global onerror/onunhandledrejection handlers have
 * `mechanism.handled === false`; manual captureException() calls have true or no
 * mechanism (both can carry an originalException, so it cannot tell them apart).
 * Keep auto-captured widget crashes; drop host-page and mixed-origin ones.
 */
function isHostOrMixedOriginAutoCapture(event: Sentry.Event): boolean {
    if (event.exception?.values?.[0]?.mechanism?.handled !== false) {
        return false;
    }
    const filenames = getEventFrameFilenames(event);
    const hasWidgetOwnedFrame = filenames.some(isWidgetOwnedScriptUrl);
    const hasThirdPartyHttpFrame = filenames.some(
        (filename) => isHttpUrl(filename) && !isWidgetOwnedScriptUrl(filename)
    );
    return !hasWidgetOwnedFrame || hasThirdPartyHttpFrame;
}

/**
 * Noise the widget never reports, checked in order; the first match drops the event
 * and logs its message. Add a new noise source here as one entry.
 */
const NOISE_FILTERS: ReadonlyArray<
    readonly [string, (event: Sentry.Event, hint: Sentry.EventHint | undefined) => boolean]
> = [
    // Staff/test traffic, dropped like the dashboard RPCs and PostHog do. Read live:
    // the target can flip to 'widget-test' after init (updateDeploymentTarget).
    ['Sentry: Filtered widget-test traffic', () => getDeploymentTarget() === 'widget-test'],
    ['Sentry: Filtered navigation/abort error', isAbortError],
    ['Sentry: Filtered crawler/bot event', isCrawlerSession],
    // Real visitors whose network blocks the API carry no scanner marker
    // and still report through ConfigProvider's failed-to-load-config error.
    ['Sentry: Filtered security scanner event', isTenableScanSession],
    ['Sentry: Filtered PostHog Blob runtime incompatibility error', isPosthogBlobUnavailableError],
    [
        'Sentry: Filtered out auto-captured non-widget or mixed-origin error',
        isHostOrMixedOriginAutoCapture,
    ],
    [
        'Sentry: Filtered out browser extension error',
        (event) => getEventFrameFilenames(event).some(isBrowserExtensionUrl),
    ],
];

const NETWORK_ERROR_NEEDLES = [
    'Failed to fetch',
    'Load failed',
    'Loading failed',
    'NetworkError',
    'CORS',
];

/**
 * Sentry `beforeSend`: drop noise (NOISE_FILTERS), then align release and
 * environment with the analytics context and add the triage tags.
 */
function beforeSend(
    event: Sentry.ErrorEvent,
    hint: Sentry.EventHint | undefined
): Sentry.ErrorEvent | null {
    const noise = NOISE_FILTERS.find(([, isNoise]) => isNoise(event, hint));
    if (noise) {
        logger.debug(noise[0]);
        return null;
    }
    if (
        getConsentMode() !== 'full' ||
        (event.timestamp !== undefined && event.timestamp <= lastConsentPromotion)
    ) {
        return scrubPrivateSentryEvent(event);
    }

    // Sentry matches sourcemaps on event.release, not on tags.
    if (!event.release && event.tags?.rw_git_commit_sha) {
        event.release = String(event.tags.rw_git_commit_sha);
        logger.debug('Sentry beforeSend: Set event.release from tag:', {
            release: event.release,
        });
    }

    // Use the actual IX_ENVIRONMENT from the backend, set as the rw_environment
    // tag by setSentryAnalyticsContext().
    if (event.tags?.rw_environment) {
        const newEnvironment = event.tags.rw_environment as string;
        logger.debug('Sentry beforeSend: Setting environment from rw_environment tag:', {
            oldEnvironment: event.environment,
            newEnvironment: newEnvironment,
            allTags: event.tags,
        });
        event.environment = newEnvironment;
    } else {
        logger.debug('Sentry beforeSend: No rw_environment tag found:', {
            currentEnvironment: event.environment,
            allTags: event.tags,
        });
    }

    const posthogInitErrorCode = getPosthogInitErrorCode(hint);
    if (posthogInitErrorCode) {
        event.tags = { ...event.tags, posthog_init_error_code: posthogInitErrorCode };
    }

    if (!event.exception) {
        return event;
    }

    const streamPhase = stringProp(event.extra, 'streamPhase');
    const errorCategory = stringProp(event.extra, 'errorCategory');
    if (streamPhase) {
        event.tags = { ...event.tags, stream_phase: streamPhase };
    }
    if (errorCategory) {
        event.tags = { ...event.tags, error_category: errorCategory };
    }

    // Flag likely network errors (like CORS) for triage.
    const errorMessage = event.exception.values?.[0]?.value || '';
    if (
        NETWORK_ERROR_NEEDLES.some((needle) => errorMessage.includes(needle)) ||
        errorCategory === 'network_stream_failure'
    ) {
        event.tags = { ...event.tags, likely_network_error: true };
    }

    return event;
}

/**
 * ROSE-FRONT-21W: a Tenable Web App Scanning run on a client's site blocks the
 * widget's API calls, so every scanned page reported "ConfigProvider: failed to
 * load config". The scanner's Chrome sends no bot user agent, but its browser
 * carries a taint-tracer global, which its payload URLs also name.
 */
const TENABLE_WAS_MARKER = '_tenable_wasscan_js_namespace_taint_tracer';

function isTenableScanSession(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return (
            TENABLE_WAS_MARKER in window ||
            TENABLE_WAS_MARKER in (window.top ?? window) ||
            window.location.href.includes(TENABLE_WAS_MARKER)
        );
    } catch {
        // Cross-origin window.top throws: fall back to this frame's URL.
        return window.location.href.includes(TENABLE_WAS_MARKER);
    }
}

/**
 * Get the Rose Widget Sentry client instance
 *
 * This provides access to the isolated Sentry instance for the InboundX widget.
 * Returns null if Sentry has not been initialized.
 *
 * @returns The Sentry client instance or null
 */
export function getRoseSentryClient(): ReturnType<typeof Sentry.getClient> | null {
    return inboundxSentryClient;
}

/**
 * Initialize Sentry error tracking with namespace isolation
 *
 * This creates an isolated Sentry instance to avoid conflicts with
 * the host page's Sentry installation.
 *
 * Note: The environment is automatically set later via setSentryAnalyticsContext()
 * from the backend API (IX_ENVIRONMENT). Do not pass deployment target as environment.
 *
 * @param dsn - Sentry DSN from environment variable
 */
export function initSentry(dsn?: string): void {
    // Check if our namespaced instance is already initialized
    if (inboundxSentryClient) {
        logger.debug('Sentry: InboundX instance already initialized, skipping');
        return;
    }

    // Check if DSN is provided
    if (!dsn || dsn.trim() === '') {
        logger.debug('Sentry: DSN not provided, skipping initialization');
        return;
    }

    try {
        const deploymentTarget = getDeploymentTarget();

        // Get version number and Git SHA from build variables
        // Version from constants, Git SHA injected at build time via Vite's define plugin
        const version = FRONTEND_VERSION;
        const gitCommitSha =
            typeof __INBOUNDX_GIT_COMMIT_SHA__ !== 'undefined'
                ? __INBOUNDX_GIT_COMMIT_SHA__
                : 'unknown';

        // Check if DSN is a tunnel/proxy URL (contains userose.ai domain)
        // If so, use it as tunnel and get the actual Sentry DSN from a global variable
        // The actual DSN should be injected at build time via __INBOUNDX_SENTRY_DSN_REAL__
        const isTunnelUrl = dsn.includes('userose.ai');

        // When using tunnel, the real Sentry DSN must be available
        // Do NOT fallback to the tunnel URL as DSN - it's not a valid Sentry DSN
        if (isTunnelUrl) {
            const hasRealDsn =
                typeof __INBOUNDX_SENTRY_DSN_REAL__ !== 'undefined' &&
                __INBOUNDX_SENTRY_DSN_REAL__ &&
                __INBOUNDX_SENTRY_DSN_REAL__.trim() !== '';

            if (!hasRealDsn) {
                logger.error(
                    'Sentry: Tunnel URL provided but real DSN not available. Sentry will not be initialized.',
                    {
                        tunnelUrl: dsn,
                        note: 'Set __INBOUNDX_SENTRY_DSN_REAL__ in vite.config.ts define section',
                    }
                );
                return;
            }
        }

        const actualDsn = isTunnelUrl ? __INBOUNDX_SENTRY_DSN_REAL__ : dsn;
        const tunnel = isTunnelUrl ? dsn : undefined;
        const sentryApplicationKey =
            typeof __INBOUNDX_SENTRY_APPLICATION_KEY__ !== 'undefined' &&
            __INBOUNDX_SENTRY_APPLICATION_KEY__.trim() !== ''
                ? __INBOUNDX_SENTRY_APPLICATION_KEY__.trim()
                : SENTRY_BUNDLER_APPLICATION_KEY_FALLBACK;
        const hasSentryModuleMetadata =
            typeof globalThis !== 'undefined' &&
            !!(globalThis as { _sentryModuleMetadata?: unknown })._sentryModuleMetadata;

        // Initialize with isolated scope to avoid conflicts with host page
        Sentry.init({
            dsn: actualDsn,
            // Tunnel option: routes all events through our proxy to bypass ad-blockers
            // When set, the SDK will send events to this URL instead of directly to Sentry
            tunnel,
            // Setting this option to true will send default PII data to Sentry.
            // For example, automatic IP address collection on events
            sendDefaultPii: false,

            // Use version number as release (matches sourcemaps uploaded in release command)
            // This ensures runtime events and uploaded sourcemaps share the same release identifier
            release: version,

            // Explicitly set environment to undefined to prevent Sentry from using "production" as default
            // It will be set later via setSentryAnalyticsContext() with actual IX_ENVIRONMENT from backend
            environment: undefined,

            // Add deployment target, Git SHA, and namespace identifier as tags
            initialScope: {
                tags: {
                    deployment: deploymentTarget || 'unknown',
                    git_commit_sha: gitCommitSha, // For debugging/reference (commits associated via CLI)
                    inboundx_widget: 'true', // Namespace identifier
                },
            },

            // Sampling rates
            sampleRate: 0.5, // 50% of error events
            tracesSampleRate: 0, // Tracing disabled — using Google Cloud for backend tracing
            // IX-5784: no client-report envelopes through the /monitoring tunnel.
            sendClientReports: false,
            beforeBreadcrumb: (breadcrumb) => (getConsentMode() === 'full' ? breadcrumb : null),

            // Strip any replay/tracing integrations that may sneak in via defaults or SDK updates.
            // BrowserSession (Release Health) sent one envelope per page load and navigation,
            // most of the tunnel's Worker traffic (IX-5784).
            integrations: (defaults) =>
                defaults
                    .filter(
                        (i) =>
                            !i.name.toLowerCase().includes('replay') &&
                            i.name !== 'BrowserTracing' &&
                            i.name !== 'BrowserSession'
                    )
                    .concat(
                        hasSentryModuleMetadata
                            ? [
                                  Sentry.thirdPartyErrorFilterIntegration({
                                      filterKeys: [sentryApplicationKey],
                                      behaviour:
                                          'drop-error-if-exclusively-contains-third-party-frames',
                                      ignoreSentryInternalFrames: true,
                                  }),
                              ]
                            : []
                    ),

            // Restrict auto-captured stack frames to widget-owned script origins.
            allowUrls: WIDGET_OWNED_SCRIPT_ALLOW_URLS,

            // Ignore common extension noise patterns at event-filter level.
            ignoreErrors: ['chrome-extension://', 'moz-extension://'],

            // Filter out noise from third-party scripts
            beforeSend,
        });

        // Store our client reference for namespace isolation
        inboundxSentryClient = Sentry.getClient();

        logger.info('Sentry: Initialized', {
            deployment: deploymentTarget,
            release: version,
            tunnel: !!tunnel,
        });
    } catch (error) {
        logger.error('Sentry: Failed to initialize', error, {
            dsn: dsn,
        });
    }
}

/**
 * Get the Sentry Error Boundary component
 *
 * Wrap your app or components with this to catch React errors
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Manually capture an exception
 */
export const captureException = Sentry.captureException;

/**
 * Manually capture a message
 */
export const captureMessage = Sentry.captureMessage;

/**
 * Set user context for Sentry
 */
export const setUser: typeof Sentry.setUser = (user) =>
    Sentry.setUser(getConsentMode() === 'full' ? user : null);

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb: typeof Sentry.addBreadcrumb = (...args) => {
    if (getConsentMode() === 'full') Sentry.addBreadcrumb(...args);
};

/**
 * Set analytics context for Sentry including versions and environment
 *
 * This should be called after analytics initialization to add
 * domain, deployment target, versions, and environment to all Sentry events.
 *
 * @param domain - The effective domain (rw_domain) - represents the client
 * @param deploymentTarget - The deployment type (rw_deployment_target)
 * @param apiVersion - Backend API version (optional)
 * @param frontendVersion - Frontend version from package.json (optional)
 * @param environment - Environment (development/production) (optional)
 * @param gitCommitSha - Git commit SHA for release tracking (optional)
 */
export function setSentryAnalyticsContext(
    domain?: string,
    deploymentTarget?: string,
    apiVersion?: string,
    frontendVersion?: string,
    environment?: string,
    gitCommitSha?: string
): void {
    if (getConsentMode() !== 'full') return;
    try {
        // Set as tags for easy filtering in Sentry UI
        if (domain) {
            Sentry.setTag('rw_domain', domain);
            Sentry.setTag('client', domain); // Also set as 'client' for easier identification
        }
        if (deploymentTarget) {
            Sentry.setTag('rw_deployment_target', deploymentTarget);
        }
        if (apiVersion) {
            Sentry.setTag('rw_api_version', apiVersion);
        }
        if (frontendVersion) {
            Sentry.setTag('rw_frontend_version', frontendVersion);
        }
        if (environment) {
            Sentry.setTag('rw_environment', environment);
            // Note: The beforeSend callback will read rw_environment tag and set it as the event environment
            logger.debug(
                'Sentry: Environment tag set (will be applied to events via beforeSend):',
                {
                    rw_environment: environment,
                }
            );
        }
        if (gitCommitSha) {
            Sentry.setTag('rw_git_commit_sha', gitCommitSha);
            // Also set as 'release' for Sentry's built-in release tracking
            Sentry.setTag('release', gitCommitSha);
        }

        // Set in analytics context for detailed view
        Sentry.setContext('analytics', {
            domain,
            deploymentTarget,
            apiVersion,
            frontendVersion,
            environment,
            gitCommitSha,
        });

        // Set client context separately for better organization in Sentry
        if (domain) {
            Sentry.setContext('client', {
                name: domain,
                domain: domain,
            });
        }

        // Set versions context for easy access to version information
        if (apiVersion || frontendVersion || gitCommitSha) {
            Sentry.setContext('versions', {
                backend: apiVersion,
                frontend: frontendVersion,
                gitCommit: gitCommitSha,
            });
        }

        logger.debug('Sentry: Analytics context set', {
            domain,
            deploymentTarget,
            apiVersion,
            frontendVersion,
            environment,
            gitCommitSha,
        });
    } catch (error) {
        logger.error('Sentry: Failed to set analytics context', error, {
            domain: domain,
        });
    }
}
